import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CategoryList from "../Blog/CategoryList";
import RecentBlogs from "../Blog/RecentBlogs";
import SearchBar from "../Blog/SearchBar";
import blogService from "../../services/blogService";

export default function BlogSidebar({ 
  showSearch = false,
  searchTerm = "",
  onSearchChange,
  selectedCategory = "",
  onCategorySelect
}) {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  
  // Use URL params if handlers not provided (for blog details page)
  const categoryFromUrl = searchParams.get('category') || "";
  const finalSelectedCategory = selectedCategory || categoryFromUrl;

  // Format date from timestamp
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await blogService.getCategories();
        if (response.success) {
          setCategories(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchRecentBlogs = async () => {
      try {
        setLoadingRecent(true);
        const response = await blogService.getAllBlogs({ 
          limit: 3,
          page: 1 
        });
        
        if (response.success && response.data) {
          // Transform API data to match RecentBlogs format
          const transformedPosts = response.data.map(blog => ({
            id: blog.id,
            date: formatDate(blog.created_at),
            title: blog.title,
            image: blog.image,
            slug: blog.slug
          }));
          setRecentPosts(transformedPosts);
        }
      } catch (err) {
        console.error('Error fetching recent blogs:', err);
        setRecentPosts([]);
      } finally {
        setLoadingRecent(false);
      }
    };

    fetchRecentBlogs();
  }, []);

  // Default handlers for blog details page (navigate to blogs page)
  const handleCategorySelect = onCategorySelect || ((category) => {
    window.location.href = `/blogs?category=${encodeURIComponent(category)}`;
  });

  const handleSearchChange = onSearchChange || ((value) => {
    if (value.trim()) {
      window.location.href = `/blogs?search=${encodeURIComponent(value)}`;
    } else {
      window.location.href = '/blogs';
    }
  });

  return (
    <div className="col-span-8 lg:col-span-4">
      {showSearch && (
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
      )}
      
      <CategoryList
        categories={categories}
        selectedCategory={finalSelectedCategory}
        onCategorySelect={handleCategorySelect}
      />
      
      {loadingRecent ? (
        <div className="mt-[54px]">
          <h1 className="font-semibold text-22 text-white">Recent Blogs</h1>
          <div className="mt-8 text-paragraph">Loading recent blogs...</div>
        </div>
      ) : recentPosts.length > 0 ? (
        <RecentBlogs recentPosts={recentPosts} />
      ) : (
        <div className="mt-[54px]">
          <h1 className="font-semibold text-22 text-white">Recent Blogs</h1>
          <div className="mt-8 text-paragraph">No recent blogs available</div>
        </div>
      )}

      {/* Banner */}
      <div className="mt-[30px] flex justify-center items-end relative w-full rounded-2xl overflow-hidden">
        <img
          src="https://quland.vercel.app/assets/images/service/detail-3.webp"
          alt="Modern IT Design Consulting Services"
          className="object-cover w-full"
        />
        <div className="absolute w-full h-full black_overlay"></div>
        <div className="w-full max-w-[277px] absolute bottom-[30px]">
          <h1 className="mb-5 font-semibold text-center text-white text-24">
            Modern IT Design Consulting Services
          </h1>
          <Link to="/contact">
            <div className="inline-flex w-full py-3 home-two-btn-bg group bg-purple border-purple">
              <svg
                className="relative z-10"
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="transition-all duration-300 group-hover:fill-purple"
                  d="M24.1094 2.17188C24.625 2.3125 25 2.73438 25 3.25C25 15.2969 15.25 25 3.25 25C2.6875 25 2.26562 24.6719 2.125 24.1562L1 19.2812C0.90625 18.7656 1.14062 18.2031 1.65625 17.9688L6.90625 15.7188C7.375 15.5312 7.89062 15.6719 8.21875 16.0469L10.5625 18.9062C14.2188 17.1719 17.1719 14.1719 18.8594 10.6094L16 8.26562C15.625 7.9375 15.4844 7.42188 15.6719 6.95312L17.9219 1.70312C18.1562 1.1875 18.7188 0.90625 19.2344 1.04688L24.1094 2.17188Z"
                  fill="white"
                />
              </svg>
              <span className="relative z-10 text-base text-white transition-all duration-300 group-hover:text-purple font-inter">
                +236 (456) 896 22
              </span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
